/**
 * googleElevationPoints — minimal Google Maps Elevation API client
 * that hits the proxy with an arbitrary point list. Used by the EAS
 * cascading sampler (EAS-3) for Stages 1-3.
 *
 * Pack: `docs/prompt-packs/Elevation_Averaging_Sampling_Prompt.md`.
 *
 * Why a separate helper
 * ---------------------
 * `lib/fetchPolygonElevations.ts` already wraps the same endpoint, but
 * it bakes in vertex-array semantics: it appends the centroid, caps
 * at 10 vertex samples, and enforces the EL3 "≥ ceil(N/2) returned"
 * threshold. The cascade sampler needs to issue a point list of its
 * own choosing (in-polygon grid centres, buffered-bbox grid centres,
 * etc.) and read back the raw per-point sample set without the EL3
 * threshold collapsing successful stages into nulls.
 *
 * This helper is intentionally THINNER than fetchPolygonElevations:
 *   - takes a `LatLng[]` of arbitrary points,
 *   - returns the parsed per-point samples (lat / lng / elevation),
 *   - does NOT apply EL3's "minRequired" guard (the cascade does that
 *     at the stage level — "if stage returned ≥3, succeed; else try
 *     next stage").
 *
 * Key + URL pattern matches the existing helper exactly: requests go
 * through `/api/proxy?url=<google-elevation-url>` and `authedFetch`
 * attaches the Firebase bearer token (PH-0.3.2). The Google API key
 * is injected server-side — never in the client URL (CLAUDE.md).
 *
 * Telemetry
 * ---------
 * Emits a `[provider:elevation]` request/response breadcrumb on every
 * call. Buckets line up with the existing vocabulary so Cloud Run
 * logs can grep across helpers.
 */

import type { LatLng } from '../../../lib/geometry/sampleGrid';
import { rethrowIfAborted, throwIfAborted } from '../../../lib/abortSignal';
import { fetchWithTimeout } from '../types';

const ELEVATION_ENDPOINT = 'https://maps.googleapis.com/maps/api/elevation/json';

/** Output sample shape — same as the cascade sampler's `samples[]`. */
export interface GoogleElevationSample {
  lat: number;
  lng: number;
  elevationMeters: number;
}

interface ElevationApiResult {
  elevation?: number;
  location?: { lat: number; lng: number };
}

interface ElevationApiResponse {
  results?: ElevationApiResult[];
  status?: string;
  error_message?: string;
}

type Bucket =
  | 'ok'
  | 'empty-results'
  | 'parse-error'
  | 'network-error'
  | 'non-200';

function logTelemetry(phase: 'request' | 'response', payload: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.info(`[provider:elevation] ${phase}`, {
    caller: 'googleElevationPoints',
    ...payload,
  });
}

/**
 * Fetch elevations for a list of points via the Google Maps Elevation
 * API (through the server proxy). Returns the parsed sample list, or
 * `null` on any non-recoverable failure (network, parse, non-200).
 * An empty `results` array maps to `[]` (not null) — the cascade
 * stage layer is the right place to decide "empty ⇒ try next stage".
 *
 * Provider/network failures degrade to `[]`. An explicit caller abort is the
 * exception: its reason is rethrown so superseded Studio work stops rather than
 * being misreported as an empty elevation response.
 *
 * @param points     Sample points (1..N). Caller is responsible for
 *                   coord-validity; an empty input returns `[]`.
 * @param fetchImpl  Optional fetch override for testing.
 * @param signal     Attempt-owned cancellation signal.
 */
export async function fetchGoogleElevationForPoints(
  points: readonly LatLng[],
  fetchImpl?: typeof fetch,
  signal?: AbortSignal,
): Promise<GoogleElevationSample[]> {
  throwIfAborted(signal);
  if (!Array.isArray(points) || points.length === 0) {
    return [];
  }
  const locs = points.map(p => `${p.lat},${p.lng}`).join('|');

  logTelemetry('request', {
    samplePoints: points.length,
    firstPoint: { lat: points[0].lat, lng: points[0].lng },
  });

  try {
    // Route through the server proxy — the API key is injected there.
    const targetUrl = `${ELEVATION_ENDPOINT}?locations=${locs}`;
    // PH-0.3.2 — /api/proxy requires a Firebase bearer token. Import
    // dynamically so the helper stays tree-shakeable from server-side
    // call sites (none today, but lib/fetchPolygonElevations follows
    // the same pattern).
    let res: Response;
    if (fetchImpl) {
      // Test path: preserve explicit fetch override behavior.
      res = await fetchImpl(
        `/api/proxy?url=${encodeURIComponent(targetUrl)}`,
        signal ? { signal } : undefined,
      );
    } else {
      // Runtime path: fetchWithTimeout auto-routes via proxy in browser and
      // direct upstream (with server key injection) in Cloud Run Jobs.
      res = await fetchWithTimeout(targetUrl, 30000, signal);
    }
    throwIfAborted(signal);

    let json: ElevationApiResponse;
    try {
      json = (await res.json()) as ElevationApiResponse;
      throwIfAborted(signal);
    } catch (e) {
      rethrowIfAborted(signal, e);
      logTelemetry('response', {
        bucket: 'parse-error' as Bucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      return [];
    }

    const results = json.results ?? [];
    const samples: GoogleElevationSample[] = [];
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      const elev = r.elevation;
      if (typeof elev !== 'number' || !Number.isFinite(elev)) continue;
      // Prefer the API's echoed `location`, but fall back to the
      // request-side point if the response stripped it (some proxy
      // responses elide that field).
      const apiLat = r.location?.lat;
      const apiLng = r.location?.lng;
      const reqPoint = points[i];
      const lat = typeof apiLat === 'number' && Number.isFinite(apiLat) ? apiLat : reqPoint?.lat;
      const lng = typeof apiLng === 'number' && Number.isFinite(apiLng) ? apiLng : reqPoint?.lng;
      if (typeof lat !== 'number' || typeof lng !== 'number') continue;
      samples.push({ lat, lng, elevationMeters: elev });
    }

    if (samples.length === 0) {
      logTelemetry('response', {
        bucket: 'empty-results' as Bucket,
        upstreamStatus: json.status ?? null,
        upstreamError: json.error_message ?? null,
        resultsLength: results.length,
      });
      return [];
    }

    logTelemetry('response', {
      bucket: 'ok' as Bucket,
      pointsRequested: points.length,
      pointsReturned: samples.length,
    });
    return samples;
  } catch (e) {
    rethrowIfAborted(signal, e);
    logTelemetry('response', {
      bucket: 'network-error' as Bucket,
      error: (e as Error)?.message ?? 'unknown',
    });
    return [];
  }
}
